import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from '../data-table';
import type { RowActivatorLink } from '../row-activator';

export type ProspectCompanyStatus =
  | 'initial'
  | 'qualified'
  | 'in_progress'
  | 'negotiation'
  | 'closed_won'
  | 'closed_lost';
export type ProspectCompanyPriority = 'low' | 'medium' | 'high' | 'urgent';
export type SalesStage =
  | 'initial_contact'
  | 'needs_assessment'
  | 'proposal_stage'
  | 'negotiation'
  | 'contract_review'
  | 'implementation_planning';

export interface ProspectCompany {
  id: string;
  name: string;
  status: ProspectCompanyStatus;
  priority: ProspectCompanyPriority;
  createdAt: string;
  lastContact?: string | undefined;
  salesStage: SalesStage;
  contactCount: number;
  [key: string]: unknown;
}

interface Props {
  data?: DataTableResponse<ProspectCompany>;
  isLoading?: boolean;
  error?: string | null;
  onProspectCompanyClick?: (company: ProspectCompany) => void;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((company: ProspectCompany) => RowActivatorLink) | undefined;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  /** Sort actually in effect, so the header arrow reflects the query. */
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
}

const getStatusVariant = (
  status: ProspectCompanyStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'initial':
      return 'secondary';
    case 'qualified':
      return 'outline';
    case 'in_progress':
      return 'outline';
    case 'negotiation':
      return 'outline';
    case 'closed_won':
      return 'default';
    case 'closed_lost':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getPriorityVariant = (
  priority: ProspectCompanyPriority
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (priority) {
    case 'low':
      return 'secondary';
    case 'medium':
      return 'outline';
    case 'high':
      return 'outline';
    case 'urgent':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatSalesStage = (stage: SalesStage): string =>
  stage
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const ProspectCompaniesTable = ({
  data,
  isLoading = false,
  error = null,
  onProspectCompanyClick,
  getRowLink,
  onFilterChange,
  onSortChange,
  currentSortKey,
  currentSortOrder,
  onPageChange,
}: Props) => {
  const columns: ColumnConfig<ProspectCompany>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => (
        <Badge variant={getStatusVariant(item.status)}>
          {item.status
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')}
        </Badge>
      ),
    },
    // Priority, sales stage, contacts and last contact are placeholders the API
    // fills with the same value for every opportunity, so there is nothing to
    // order by until they're backed by real data.
    {
      key: 'priority',
      label: 'Priority',
      sortable: false,
      render: (item) => (
        <Badge variant={getPriorityVariant(item.priority)}>
          {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'salesStage',
      label: 'Sales Stage',
      sortable: false,
      render: (item) => <span className="text-sm">{formatSalesStage(item.salesStage)}</span>,
    },
    {
      key: 'contactCount',
      label: 'Contacts',
      sortable: false,
      align: 'center',
      render: (item) => (
        <span className="inline-flex items-center justify-center rounded-full bg-muted px-2 py-1 text-xs font-medium">
          {item.contactCount}
        </span>
      ),
    },
    {
      key: 'lastContact',
      label: 'Last Contact',
      sortable: false,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground text-sm">
          {item.lastContact ? formatDate(item.lastContact) : 'Never'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground text-sm">{formatDate(item.createdAt)}</span>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select statuses',
      multiSelect: true,
      options: [
        { value: 'initial', label: 'Initial' },
        { value: 'qualified', label: 'Qualified' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'negotiation', label: 'Negotiation' },
        { value: 'closed_won', label: 'Closed Won' },
        { value: 'closed_lost', label: 'Closed Lost' },
      ],
    },
    {
      key: 'priority',
      label: 'Priority',
      placeholder: 'Select priorities',
      multiSelect: true,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' },
      ],
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      caption="Opportunities"
      filters={filters}
      isLoading={isLoading}
      error={error}
      onRowClick={onProspectCompanyClick}
      getRowLabel={(item) => `Open opportunity ${item.name}`}
      {...(getRowLink && { getRowLink })}
      {...(onFilterChange && { onFilterChange })}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      emptyMessage="No opportunities found"
      loadingMessage="Loading opportunities..."
      // Driven by the page filter state; a default declared here is a second
      // source of truth that starts out disagreeing with the query.
      {...(currentSortKey && { currentSortKey })}
      {...(currentSortOrder && { currentSortOrder })}
    />
  );
};
